import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { seoAPI } from '../../services/api';
import { ScoreBadge, SeverityBadge, shortUrl, SeoResult, SeoScan } from './seoTypes';
import SeoKeywordGate from './SeoKeywordGate';

const FIX_TIPS: Record<string, string> = {
  title: 'Write a unique title 50–60 chars with your keyword near the start.',
  meta_description: 'Write 150–160 chars, include keyword, end with a call-to-action.',
  h1: 'Add exactly one H1 heading with your primary keyword.',
  heading_hierarchy: 'Add H2/H3 subheadings to structure your content.',
  canonical: 'Add <link rel="canonical" href="…"> in <head>.',
  open_graph: 'Add og:title, og:description, og:image (1200×630px).',
  structured_data: 'Add JSON-LD schema markup (Organization or WebPage).',
  image_alt: 'Add descriptive alt text to every image.',
  robots: 'Remove "noindex" from the robots meta tag.',
  viewport: 'Add <meta name="viewport" content="width=device-width, initial-scale=1">.',
  content_length: 'Expand to 300+ words with thorough, relevant content.',
  internal_links: 'Add 3+ internal links with descriptive anchor text.',
  https: 'Install SSL and redirect all HTTP → HTTPS.',
  twitter_card: 'Add twitter:card, twitter:title, twitter:description, twitter:image.',
  keyword_in_title: 'Include your target keyword naturally near the start of the title.',
  keyword_in_meta: 'Include your keyword in the meta description.',
  keyword_in_h1: 'Include your target keyword in the H1 heading.',
};

const SeoSummaryPage: React.FC = () => {
  const { currentAsset } = useAuth();
  const assetId = currentAsset?.id;
  const navigate = useNavigate();

  const [scan, setScan] = useState<SeoScan | null>(null);
  const [results, setResults] = useState<SeoResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const [seoPrefs, setSeoPrefs] = useState({ daily_enabled: false, weekly_enabled: false });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);

  useEffect(() => {
    if (!assetId) return;
    setLoading(true);
    seoAPI.getResults(assetId)
      .then(res => {
        setScan(res.data.data.scan);
        setResults(res.data.data.results || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [assetId]);

  useEffect(() => {
    if (!assetId) return;
    setPrefsLoading(true);
    seoAPI.getPreferences(assetId)
      .then(res => setSeoPrefs(res.data?.data || { daily_enabled: false, weekly_enabled: false }))
      .catch(() => {})
      .finally(() => setPrefsLoading(false));
  }, [assetId]);

  const handleTogglePref = async (key: 'daily_enabled' | 'weekly_enabled') => {
    if (!assetId || prefsSaving) return;
    const updated = { ...seoPrefs, [key]: !seoPrefs[key] };
    setSeoPrefs(updated);
    setPrefsSaving(true);
    try {
      await seoAPI.setPreferences(assetId, updated);
      toast.success('Preference saved');
    } catch {
      setSeoPrefs(seoPrefs);
      toast.error('Failed to save preference');
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleStartScan = async () => {
    if (!assetId) return;
    setStarting(true);
    try {
      await seoAPI.startScan(assetId);
      toast.success('SEO scan started!');
      navigate('/seo/scanner');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to start scan');
      setStarting(false);
    }
  };

  if (!assetId) return <DashboardLayout><div className="p-4 text-muted">Select an asset to use SEO tools.</div></DashboardLayout>;

  // ── Stats from latest scan ─────────────────────────────────────────────────
  const completed = results.filter(r => r.status === 'completed');
  const avgScore = completed.length
    ? Math.round(completed.reduce((s, r) => s + (r.seo_score || 0), 0) / completed.length)
    : 0;
  const allIssues = completed.flatMap(r => r.issues || []);
  const critCount = allIssues.filter(i => i.severity === 'critical').length;
  const warnCount = allIssues.filter(i => i.severity === 'warning').length;
  const infoCount = allIssues.filter(i => i.severity === 'info').length;
  const good = completed.filter(r => r.seo_score >= 80).length;
  const fair = completed.filter(r => r.seo_score >= 50 && r.seo_score < 80).length;
  const poor = completed.filter(r => r.seo_score < 50).length;

  // Top issues (most common critical/warning)
  const issueFreq: Record<string, { key: string; msg: string; sev: string; count: number }> = {};
  for (const issue of allIssues.filter(i => i.severity !== 'info')) {
    const key = issue.factor;
    if (!issueFreq[key]) issueFreq[key] = { key, msg: issue.message, sev: issue.severity, count: 0 };
    issueFreq[key].count++;
  }
  const topIssues = Object.values(issueFreq).sort((a, b) => b.count - a.count).slice(0, 6);

  // Score ring color
  const ringColor = avgScore >= 80 ? '#10b981' : avgScore >= 50 ? '#f59e0b' : '#ef4444';
  const ringBg = avgScore >= 80 ? '#d1fae5' : avgScore >= 50 ? '#fef3c7' : '#fee2e2';

  return (
    <DashboardLayout>
      <SeoKeywordGate assetId={assetId}>
      <div className="container-fluid py-3" style={{ maxWidth: 1100 }}>
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-search-heart text-primary me-2"></i>SEO Summary
            </h4>
            <p className="text-muted small mb-0">Overview of your site's SEO health</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleStartScan} disabled={starting}>
            {starting
              ? <><span className="spinner-border spinner-border-sm me-1"></span>Starting…</>
              : <><i className="bi bi-play-fill me-1"></i>New Scan</>}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
        ) : !scan ? (
          /* ── No scan yet ── */
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5">
              <i className="bi bi-search" style={{ fontSize: '3rem', color: '#cbd5e1' }}></i>
              <h5 className="mt-3 fw-bold">No SEO scan yet</h5>
              <p className="text-muted mb-4" style={{ maxWidth: 420, margin: '0 auto' }}>
                Run your first scan to get a full SEO health report — score, issues, and AI-powered fix suggestions for your top public pages.
              </p>
              <button className="btn btn-primary px-4" onClick={handleStartScan} disabled={starting}>
                {starting
                  ? <><span className="spinner-border spinner-border-sm me-2"></span>Starting…</>
                  : <><i className="bi bi-play-fill me-2"></i>Start SEO Scan</>}
              </button>
              <div className="mt-3">
                <Link to="/seo/scanner" className="text-muted small">View scan history →</Link>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* ── Score overview cards ── */}
            <div className="row g-3 mb-4">
              {/* Big score */}
              <div className="col-12 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body d-flex flex-column align-items-center justify-content-center py-4">
                    <div style={{
                      width: 90, height: 90, borderRadius: '50%',
                      background: ringBg, border: `5px solid ${ringColor}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      marginBottom: 8,
                    }}>
                      <span style={{ fontSize: '1.6rem', fontWeight: 800, color: ringColor }}>{avgScore}</span>
                    </div>
                    <div className="fw-semibold">Avg SEO Score</div>
                    <div className="small text-muted">{completed.length} pages scanned</div>
                    <div className="small text-muted mt-1">
                      {scan.completed_at ? `Last scan ${new Date(scan.completed_at).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                </div>
              </div>

              {/* Issue counts */}
              <div className="col-12 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-uppercase text-muted fw-semibold mb-3">Issues Found</div>
                    {[
                      { label: 'Critical', count: critCount, bg: '#fee2e2', color: '#dc2626', icon: 'bi-x-circle-fill' },
                      { label: 'Warnings', count: warnCount, bg: '#fef3c7', color: '#d97706', icon: 'bi-exclamation-triangle-fill' },
                      { label: 'Info', count: infoCount, bg: '#dbeafe', color: '#2563eb', icon: 'bi-info-circle-fill' },
                    ].map(s => (
                      <div key={s.label} className="d-flex align-items-center justify-content-between py-1">
                        <span className="small">
                          <i className={`bi ${s.icon} me-1`} style={{ color: s.color }}></i>{s.label}
                        </span>
                        <span style={{ background: s.bg, color: s.color, fontWeight: 700, fontSize: '0.78rem', padding: '1px 8px', borderRadius: 12 }}>
                          {s.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Page health distribution */}
              <div className="col-12 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-uppercase text-muted fw-semibold mb-3">Page Health</div>
                    {[
                      { label: 'Good (80+)', count: good, color: '#10b981', bg: '#d1fae5' },
                      { label: 'Fair (50-79)', count: fair, color: '#f59e0b', bg: '#fef3c7' },
                      { label: 'Poor (<50)', count: poor, color: '#ef4444', bg: '#fee2e2' },
                    ].map(s => (
                      <div key={s.label} className="mb-2">
                        <div className="d-flex justify-content-between small mb-1">
                          <span>{s.label}</span>
                          <span style={{ color: s.color, fontWeight: 700 }}>{s.count}</span>
                        </div>
                        <div className="progress" style={{ height: 5 }}>
                          <div className="progress-bar" style={{
                            width: `${completed.length ? (s.count / completed.length) * 100 : 0}%`,
                            background: s.color,
                          }}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick links */}
              <div className="col-12 col-md-3">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-uppercase text-muted fw-semibold mb-3">Quick Actions</div>
                    <div className="d-flex flex-column gap-2">
                      <Link to="/seo/scanner" className="btn btn-outline-primary btn-sm text-start">
                        <i className="bi bi-clock-history me-2"></i>View Scan History
                      </Link>
                      <Link to="/seo/optimizer" className="btn btn-outline-secondary btn-sm text-start">
                        <i className="bi bi-stars me-2"></i>AI Optimizer
                      </Link>
                      <button className="btn btn-outline-success btn-sm text-start" onClick={handleStartScan} disabled={starting}>
                        <i className="bi bi-arrow-clockwise me-2"></i>Re-scan Now
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Top issues ── */}
            {topIssues.length > 0 && (
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-header bg-white border-0 pt-3 pb-0">
                  <h6 className="fw-bold mb-0">
                    <i className="bi bi-exclamation-triangle text-warning me-2"></i>Most Common Issues
                  </h6>
                  <p className="text-muted small mb-2">Fixing these will have the highest impact across your pages</p>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Issue</th>
                          <th style={{ width: 90 }}>Severity</th>
                          <th style={{ width: 90 }}>Pages</th>
                          <th>How to Fix</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topIssues.map((iss, i) => (
                          <tr key={i}>
                            <td className="small">{iss.msg}</td>
                            <td><SeverityBadge severity={iss.sev} count={1} label={iss.sev} /></td>
                            <td className="small fw-semibold">{iss.count} / {completed.length}</td>
                            <td className="small" style={{ color: '#0369a1' }}>
                              {FIX_TIPS[iss.key as string] || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Page scores ── */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 d-flex align-items-center justify-content-between pt-3">
                <h6 className="fw-bold mb-0">
                  <i className="bi bi-list-check text-primary me-2"></i>Page Scores
                </h6>
                <Link to="/seo/scanner" className="btn btn-sm btn-outline-primary">
                  View full report →
                </Link>
              </div>
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Page</th>
                      <th style={{ width: 90 }}>Score</th>
                      <th style={{ width: 80 }}>Critical</th>
                      <th style={{ width: 80 }}>Warnings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completed.slice(0, 8).map(r => {
                      const crit = (r.issues || []).filter(i => i.severity === 'critical').length;
                      const warn = (r.issues || []).filter(i => i.severity === 'warning').length;
                      return (
                        <tr key={r.id}>
                          <td className="small">
                            <div className="text-truncate" style={{ maxWidth: 380 }}>
                              <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                {shortUrl(r.url)}
                              </a>
                            </div>
                            {r.page_title && <div className="text-muted" style={{ fontSize: '0.7rem' }}>{r.page_title}</div>}
                          </td>
                          <td><ScoreBadge score={r.seo_score} /></td>
                          <td>{crit > 0 && <SeverityBadge severity="critical" count={crit} />}</td>
                          <td>{warn > 0 && <SeverityBadge severity="warning" count={warn} />}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
        {/* Notification preferences */}
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <h6 className="fw-bold mb-1"><i className="bi bi-bell text-primary me-2"></i>SEO Email Notifications</h6>
            <p className="text-muted small mb-3">Receive automated SEO reports by email.</p>
            {prefsLoading ? (
              <div className="spinner-border spinner-border-sm text-secondary" role="status"></div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {([
                  { key: 'daily_enabled' as const, label: 'Daily SEO Report', desc: 'Receive a daily SEO score update via email at 9:00 AM' },
                  { key: 'weekly_enabled' as const, label: 'Weekly SEO Summary', desc: 'Receive a weekly SEO summary every Monday with recommendations' },
                ] as const).map(pref => (
                  <div key={pref.key} className="d-flex align-items-center justify-content-between p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '14px' }}>{pref.label}</div>
                      <div className="text-muted" style={{ fontSize: '12px' }}>{pref.desc}</div>
                    </div>
                    <div
                      onClick={() => handleTogglePref(pref.key)}
                      role="switch"
                      aria-checked={seoPrefs[pref.key]}
                      style={{
                        width: 44, height: 24, borderRadius: 12, cursor: prefsSaving ? 'not-allowed' : 'pointer',
                        background: seoPrefs[pref.key] ? '#6366f1' : '#cbd5e1',
                        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                      }}
                    >
                      <div style={{
                        position: 'absolute', top: 3, left: seoPrefs[pref.key] ? 23 : 3,
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                      }}></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      </SeoKeywordGate>
    </DashboardLayout>
  );
};

export default SeoSummaryPage;
